import { useQuery } from '@tanstack/react-query'
import { api } from '@api'
import { useCurrentUserStore } from '@store'
import type { PaginatedResponse } from './types'
import type { Notification } from './types/notificationTypes'
import { NOTIFICATIONS_PAGE_VERSION } from './usePaginateNotifications'

// Poll cadence for the unread badge. The API has no realtime channel (push
// only), and web FCM is a no-op, so a short interval keeps the badge close to
// real time on the web menu without a websocket. On native, an incoming FCM
// push also invalidates the `notifications` query root (see
// `invalidateQueriesOnPush`), which refreshes this count instantly.
const UNREAD_REFETCH_INTERVAL_MS = 30_000

// Shared with `invalidateQueriesOnPush` / `useMarkNotificationAsRead`, which
// invalidate the `notifications` root — this key is a child of it, so both a
// push and a mark-as-read refresh the badge automatically.
export const UNREAD_NOTIFICATIONS_QUERY_KEY = ['notifications', 'unread-count'] as const

const fetchUnreadCount = async (): Promise<number> => {
  // `limit=1` keeps the payload tiny; the unread total is computed server-side
  // over every row (not just the page) and returned in the metadata.
  const { data } = await api.get<PaginatedResponse<Notification>>(
    `/notifications?tab=all&limit=1&v=${NOTIFICATIONS_PAGE_VERSION}`,
  )
  return data.metadata?.countsByTab?.all?.unread ?? 0
}

/**
 * Unread notification count for the current user, used to badge the
 * "Notifications" entry in the navigation menu. Keeps the last known count
 * while a refresh fails; 0 when none is known (first fetch not resolved or
 * failed, logged out).
 */
export const useUnreadNotificationsCount = (): { unreadCount: number } => {
  const { userID } = useCurrentUserStore()

  const { data: unreadCount = 0 } = useQuery({
    queryKey: UNREAD_NOTIFICATIONS_QUERY_KEY,
    queryFn: fetchUnreadCount,
    enabled: !!userID,
    refetchInterval: UNREAD_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  })

  return { unreadCount }
}
